import type { RevitFamily } from '../../api/revit/types';
import {
  getEquivalentRevitParameterNames,
  sanitizeRevitParameterNames,
} from '../revitParameterNames';
import type { ParameterTypeHint } from './projectParameterMutationPlan';

export type FamilyParameterBindingKind = 'type' | 'instance';

export interface FamilyParameterMutationTargetSelection {
  hasReliableSnapshot: boolean;
  matchingFamilyCount: number;
  compatibleFamilyCount: number;
  targetFamilyIds: string[];
  matchingFamilies: RevitFamily[];
  compatibleFamilies: RevitFamily[];
  targetFamilies: RevitFamily[];
}

interface FamilyParameterDescriptor {
  bindingKind: FamilyParameterBindingKind;
  dataType?: string;
}

function normalizeDataType(dataType: string | undefined): string | null {
  switch (dataType?.trim().toUpperCase()) {
    case 'TEXT':
    case 'STRING':
      return 'TEXT';
    case 'INTEGER':
      return 'INTEGER';
    case 'NUMBER':
    case 'DOUBLE':
      return 'NUMBER';
    case 'LENGTH':
      return 'LENGTH';
    case 'ANGLE':
      return 'ANGLE';
    case 'MULTILINETEXT':
    case 'MULTILINE TEXT':
      return 'MULTILINETEXT';
    case 'PIPE_MASS':
    case 'PIPEMASS':
    case 'PIPE MASS':
      return 'PIPE_MASS';
    case 'YESNO':
    case 'BOOLEAN':
      return 'YESNO';
    default:
      return null;
  }
}

/** Returns whether the client can reason about the supplied Revit data type. */
export function isKnownDataType(dataType: string | undefined): boolean {
  return normalizeDataType(dataType) !== null;
}

// YesNo and Integer share StorageType.Integer, and Number accepts any integer
// value — so for setup purposes YESNO and NUMBER both satisfy an INTEGER
// expectation (and INTEGER satisfies YESNO/NUMBER). Cross-group pairs (YESNO
// vs NUMBER) stay incompatible: a checkbox is not a generic number.
const INTEGER_STORAGE_TYPES = new Set(['INTEGER', 'YESNO']);
const NUMERIC_STORAGE_TYPES = new Set(['INTEGER', 'NUMBER']);

export function areDataTypesCompatible(actual: string | undefined, expected: string | null): boolean {
  const normalizedActual = normalizeDataType(actual);
  const normalizedExpected = normalizeDataType(expected ?? undefined);
  if (normalizedActual === normalizedExpected) {
    return true;
  }

  // A future expected type this client does not yet know
  // is not evidence of a mismatch — the host's familyHasCompatibleParameter
  // treats it as compatible, and demoting on it re-reported present parameters
  // as missing forever (the #PCF_ISO_MESSSAGE loop).
  if (normalizedExpected === null) {
    return true;
  }

  // The client cannot establish that an existing parameter satisfies a known
  // requested type when the snapshot omits or cannot identify its data type.
  // Keep the family in the host validation request instead of silently
  // accepting an incompatible definition.
  if (normalizedActual === null) {
    return false;
  }

  return (
    (INTEGER_STORAGE_TYPES.has(normalizedExpected) && INTEGER_STORAGE_TYPES.has(normalizedActual)) ||
    (NUMERIC_STORAGE_TYPES.has(normalizedExpected) && NUMERIC_STORAGE_TYPES.has(normalizedActual))
  );
}

export function normalizeFamilyCategoryKey(category: string | undefined): string {
  return (category || '')
    .trim()
    .replace(/\s+/g, '')
    .toUpperCase();
}

export function getFamilyParameterBindingKind(
  family: RevitFamily,
  parameterName: string
): FamilyParameterBindingKind | null {
  return getFamilyParameterDescriptor(family, parameterName)?.bindingKind ?? null;
}

function getFamilyParameterDescriptor(
  family: RevitFamily,
  parameterName: string
): FamilyParameterDescriptor | null {
  const normalizedParameterName = parameterName.trim().toLowerCase();
  const normalizedParameterNames = new Set(
    getEquivalentRevitParameterNames(parameterName).map((name) => name.trim().toLowerCase())
  );
  if (normalizedParameterNames.size === 0) {
    return null;
  }

  let equivalentDescriptor: FamilyParameterDescriptor | null = null;
  const familyTypes = Array.isArray(family.types) ? family.types : [];
  for (const familyType of familyTypes) {
    const parameterValues = Array.isArray(familyType?.parameterValues)
      ? familyType.parameterValues
      : [];
    for (const parameterValue of parameterValues) {
      const candidateName = parameterValue.parameterName?.trim().toLowerCase() ?? '';
      if (!normalizedParameterNames.has(candidateName)) {
        continue;
      }

      const descriptor: FamilyParameterDescriptor = {
        bindingKind: parameterValue.isInstance ? 'instance' : 'type',
        dataType: parameterValue.dataType,
      };
      if (candidateName === normalizedParameterName) {
        return descriptor;
      }

      equivalentDescriptor ??= descriptor;
    }
  }

  const familyParameters = Array.isArray(family.parameters) ? family.parameters : [];
  for (const parameter of familyParameters) {
    const candidateName = parameter.name?.trim().toLowerCase() ?? '';
    if (!normalizedParameterNames.has(candidateName)) {
      continue;
    }

    const descriptor: FamilyParameterDescriptor = {
      bindingKind: parameter.isInstance ? 'instance' : 'type',
      dataType: parameter.dataType,
    };
    if (candidateName === normalizedParameterName) {
      return descriptor;
    }

    equivalentDescriptor ??= descriptor;
  }

  return equivalentDescriptor;
}

export interface FamilyParameterDescriptorInfo {
  bindingKind: FamilyParameterBindingKind;
  dataType?: string;
}

/** Public read of the per-family parameter descriptor used by the compatibility checks. */
export function getFamilyParameterDescriptorInfo(
  family: RevitFamily,
  parameterName: string
): FamilyParameterDescriptorInfo | null {
  return getFamilyParameterDescriptor(family, parameterName);
}

export function familyHasCompatibleParameter(
  family: RevitFamily,
  parameterName: string,
  bindingKind?: FamilyParameterBindingKind,
  expectedDataType?: string
): boolean {
  if (!parameterName.trim()) {
    return true;
  }

  const descriptor = getFamilyParameterDescriptor(family, parameterName);
  if (!descriptor) {
    return false;
  }

  if (bindingKind && descriptor.bindingKind !== bindingKind) {
    return false;
  }

  const normalizedExpectedType = normalizeDataType(expectedDataType);
  if (!normalizedExpectedType) {
    return true;
  }

  return areDataTypesCompatible(descriptor.dataType, normalizedExpectedType);
}

export function familyNeedsParameterMutation(
  family: RevitFamily,
  parameterNames: string[],
  bindingKind: FamilyParameterBindingKind,
  parameterTypeHints: ParameterTypeHint[] = []
): boolean {
  return sanitizeRevitParameterNames(parameterNames).some(
    (parameterName) => {
      const equivalentNames = new Set(
        getEquivalentRevitParameterNames(parameterName).map((name) => name.trim().toLowerCase())
      );
      const expectedDataType = parameterTypeHints.find((hint) =>
        equivalentNames.has(hint.name.trim().toLowerCase())
      )?.dataType;
      return !familyHasCompatibleParameter(
        family,
        parameterName,
        bindingKind,
        expectedDataType
      );
    }
  );
}

export function selectFamilyParameterMutationTargets(
  families: RevitFamily[],
  parameterNames: string[],
  bindingKind: FamilyParameterBindingKind,
  matchesTarget: (family: RevitFamily) => boolean,
  parameterTypeHints: ParameterTypeHint[] = []
): FamilyParameterMutationTargetSelection {
  if (families.length === 0) {
    return {
      hasReliableSnapshot: false,
      matchingFamilyCount: 0,
      compatibleFamilyCount: 0,
      targetFamilyIds: [],
      matchingFamilies: [],
      compatibleFamilies: [],
      targetFamilies: [],
    };
  }

  const matchingFamilies = families.filter((family) => {
    if (!family?.id || family.isSystemFamily || family.isEditable === false) {
      return false;
    }

    return matchesTarget(family);
  });

  const targetFamilies = matchingFamilies.filter((family) =>
    familyNeedsParameterMutation(family, parameterNames, bindingKind, parameterTypeHints)
  );
  const compatibleFamilies = matchingFamilies.filter(
    (family) => !targetFamilies.some((targetFamily) => targetFamily.id === family.id)
  );

  return {
    hasReliableSnapshot: true,
    matchingFamilyCount: matchingFamilies.length,
    compatibleFamilyCount: compatibleFamilies.length,
    targetFamilyIds: Array.from(new Set(targetFamilies.map((family) => family.id).filter(Boolean))),
    matchingFamilies,
    compatibleFamilies,
    targetFamilies,
  };
}
